/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Layer, Point } from 'paper';
import { GraphicalTakeOffCommandService } from '../../graphical-take-off-command.service';
import { GraphicalTakeOffFormState } from '../../graphical-take-off-form.service';
import {
  getGraphicalTakeOffImageLastRotation,
  getGraphicalTakeOffViewImagePipeline,
  GraphicalTakeOffBusinessService, newGraphicalTakeOffImageRotateTransformation,
} from '../../resources';

interface GraphicalTakeOffLevelVector {
  direction: string;
  distance: number;
}

const BOUND_DIRECTIONS = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];

const getCursorClass = (direction: string) => `level-cursor-${direction.toLowerCase()}`;

/**
 * Level mode component
 */
@Component({
  standalone: false,
  selector: 'gh-graphical-take-off-level',
  templateUrl: 'graphical-take-off-level.component.html',
  styleUrls: ['graphical-take-off-level.component.scss'],
})
export class GraphicalTakeOffLevelComponent implements OnInit {
  /**
   * Level mode change event
   *
   * @type {EventEmitter<number>}
   */
  @Output() onLevel: EventEmitter<number> = new EventEmitter<number>();

  /**
   * Rotation degree
   *
   * @type {number}
   */
  degree: number = 0;

  /**
   * Previous rotation angle
   *
   * @type {number}
   */
  prevRotationAngle: number = 0;

  private rasterLayer: Layer;

  /**
   * Constructor
   *
   * @param {GraphicalTakeOffBusinessService} gtoService GTO service
   */
  constructor(private form: GraphicalTakeOffFormState,
              private commands: GraphicalTakeOffCommandService) {
    this.onMouseDrag = this.onMouseDrag.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
  }

  /**
   * Component init
   *
   * @returns {void}
   */
  ngOnInit(): void {
    this.enterLevelMode();
    this.setCursor();
  }

  /**
   * Cancel rotation
   *
   * @returns {void}
   */
  cancel(): void {
    if (this.degree) {
      this.commands.currentViewImageSetTransformation(newGraphicalTakeOffImageRotateTransformation(this.prevRotationAngle));
      this.degree = this.getCurrentRotation();
    }

    this.leaveLevelMode();
  }

  /**
   * Apply rotation
   *
   * @returns {void}
   */
  apply(): void {
    this.leaveLevelMode();
  }

  /**
   * Enter level mode.
   * Register mouse events handlers
   *
   * @returns {void}
   */
  private enterLevelMode(): void {
    const rasterLayer = this.commands.getRasterLayer();

    this.rasterLayer = rasterLayer;

    this.prevRotationAngle = this.getCurrentRotation();
    this.degree = this.degree || this.prevRotationAngle;

    // Calculates rotation angle based on event points delta
    // and applies it to the layer rotation
    rasterLayer.on('mousedrag', this.onMouseDrag);
  }

  /**
   * Set cursor style
   *
   * @returns {void}
   */
  private setCursor(): void {
    this.rasterLayer.on('mousemove', this.onMouseMove);
    this.rasterLayer.on('mouseleave', this.onMouseLeave);
  }

  /**
   * Leave level mode
   *
   * @returns {void}
   */
  private leaveLevelMode(): void {
    const timeout = 200;

    this.rasterLayer.selected = false;
    this.rasterLayer.off('mousedrag', this.onMouseDrag);
    this.rasterLayer.off('mousemove', this.onMouseMove);
    this.rasterLayer.off('mouseleave', this.onMouseLeave);

    setTimeout(() => this.onLevel.emit(this.degree), timeout);
  }

  private getCurrentRotation(): number {
    return getGraphicalTakeOffImageLastRotation(getGraphicalTakeOffViewImagePipeline(this.form.currentView));
  }

  private onMouseMove(event: any): void {
    const canvasEl = this.commands.getCanvasElement();

    this.cleanUpLevelClasses();

    const point = event.point;
    const bounds = this.rasterLayer.bounds;
    const vectors: GraphicalTakeOffLevelVector[] = [];

    BOUND_DIRECTIONS.forEach((direction) => vectors.push({
      direction,
      distance: point.subtract(bounds[direction]).length,
    }));

    const vector = vectors.reduce((prev, curr) => prev.distance < curr.distance ? prev : curr);
    canvasEl.classList.add(getCursorClass(vector.direction));
  }
  private onMouseDrag(event: any): void {
    const centerPoint = this.rasterLayer.bounds.center;
    const dragStart = new Point(event.point.x - event.delta.x, event.point.y - event.delta.y);

    const startVector = new Point(dragStart.x - centerPoint.x, dragStart.y - centerPoint.y);
    const endVector = new Point(event.point.x - centerPoint.x, event.point.y - centerPoint.y);

    this.commands.currentViewImageTransform(newGraphicalTakeOffImageRotateTransformation(startVector.getDirectedAngle(endVector)));
    this.degree = this.getCurrentRotation();
  }

  private onMouseLeave(): void {
    this.cleanUpLevelClasses();
  }

  private cleanUpLevelClasses(): void {
    const canvasEl = this.commands.getCanvasElement();
    BOUND_DIRECTIONS.forEach((direction) => canvasEl.classList.remove(getCursorClass(direction)));
  }
}
